import { useState, useEffect } from 'react'
import AppShell from './components/layout/AppShell'
import ChatPage from './pages/ChatPage'
import AdminPage from './pages/AdminPage'
import SessionsPage from './pages/SessionsPage'
import SessionDetailPage from './pages/SessionDetailPage'
import StoryUploadPage from './pages/StoryUploadPage'
import StoriesPage from './pages/StoriesPage'
import StoryDetailPage from './pages/StoryDetailPage'
import AuditPage from './pages/AuditPage'
import AuditSessionDetailPage from './pages/AuditSessionDetailPage'
import CharactersPage from './pages/CharactersPage'
import CharacterDetailPage from './pages/CharacterDetailPage'
import PrefabsPage from './pages/PrefabsPage'
import PrefabDetailPage from './pages/PrefabsDetailPage'
import CachedResponsesPage from './pages/CachedResponsesPage'
import CachedResponseDetailPage from './pages/CachedResponseDetailPage'
import HomePage from './pages/HomePage'
import APIKeysPage from './pages/APIKeysPage'
import GarageFilesPage from './pages/GarageFilesPage'
import { WebSocketProvider } from './context/WebSocketContext'
import { ChatProvider } from './context/ChatContext'
import { A2FSettingsProvider } from './context/A2FSettingsContext'
import { useUser } from './context/UserContext'

interface PageState {
  name: string
  sessionId?: string
}

function getPage(): PageState {
  const hash = window.location.hash
  if (hash === '#admin') return { name: 'admin' }
  if (hash === '#sessions') return { name: 'sessions' }
  if (hash.startsWith('#sessions/')) return { name: 'session-detail', sessionId: hash.slice('#sessions/'.length) }
  if (hash === '#audit') return { name: 'audit' }
  if (hash.startsWith('#audit/')) return { name: 'audit-detail', sessionId: hash.slice('#audit/'.length) }
  if (hash === '#upload') return { name: 'upload' }
  if (hash === '#stories') return { name: 'stories' }
  if (hash.startsWith('#stories/')) return { name: 'story-detail', sessionId: hash.slice('#stories/'.length) }
  if (hash === '#characters') return { name: 'characters' }
  if (hash.startsWith('#characters/')) return { name: 'character-detail', sessionId: hash.slice('#characters/'.length) }
  if (hash === '#prefabs') return { name: 'prefabs' }
  if (hash.startsWith('#prefabs/')) return { name: 'prefab-detail', sessionId: hash.slice('#prefabs/'.length) }
  if (hash === '#cached-responses') return { name: 'cached-responses' }
  if (hash.startsWith('#cached-responses/')) return { name: 'cached-response-detail', sessionId: hash.slice('#cached-responses/'.length) }
  if (hash === '#home') return { name: 'home' }
  if (hash === '#api-keys') return { name: 'api-keys' }
  if (hash === '#garage') return { name: 'garage' }
  return { name: 'chat' }
}

export default function App() {
  const { isLoggedIn, isAdmin, requireApiKeys } = useUser()
  const isAuthenticated = isAdmin
  const [page, setPage] = useState<PageState>(getPage)

  useEffect(() => {
    if (page.name !== 'chat' && window.location.search) {
      window.history.replaceState(null, '', window.location.pathname + window.location.hash)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = () => {
      const next = getPage()
      if (next.name !== 'chat') {
        window.history.replaceState(null, '', window.location.pathname + window.location.hash)
      }
      setPage(next)
    }
    window.addEventListener('hashchange', handler)
    return () => window.removeEventListener('hashchange', handler)
  }, [])

  const chatPage = (
    <WebSocketProvider>
      <ChatProvider>
        <A2FSettingsProvider>
          <ChatPage />
        </A2FSettingsProvider>
      </ChatProvider>
    </WebSocketProvider>
  )

  function renderPage() {
    if (page.name === 'home') return <HomePage />

    // Flag off = open mode: no key required, everyone gets in
    // Flag on  = restricted: must have at least one key
    if (requireApiKeys && !isLoggedIn && !isAuthenticated) return <HomePage />

    // Pages accessible with any key (standard or admin)
    if (page.name === 'chat') return chatPage
    if (page.name === 'admin') return <AdminPage />
    if (page.name === 'api-keys') {
      if (!isAuthenticated) return <AdminPage />
      return <APIKeysPage />
    }

    // Admin-only pages — standard key users fall back to chat
    if (!isAuthenticated) return chatPage
    if (page.name === 'sessions') return <SessionsPage />
    if (page.name === 'session-detail' && page.sessionId) return <SessionDetailPage sessionId={page.sessionId} />
    if (page.name === 'audit') return <AuditPage />
    if (page.name === 'audit-detail' && page.sessionId) return <AuditSessionDetailPage sessionId={page.sessionId} />
    if (page.name === 'upload') return <StoryUploadPage />
    if (page.name === 'stories') return <StoriesPage />
    if (page.name === 'story-detail' && page.sessionId) return <StoryDetailPage filename={decodeURIComponent(page.sessionId)} />
    if (page.name === 'characters') return <CharactersPage />
    if (page.name === 'character-detail' && page.sessionId) return <CharacterDetailPage characterId={page.sessionId} />
    if (page.name === 'prefabs') return <PrefabsPage />
    if (page.name === 'prefab-detail' && page.sessionId) return <PrefabDetailPage prefabId={page.sessionId} />
    if (page.name === 'cached-responses') return <CachedResponsesPage />
    if (page.name === 'cached-response-detail' && page.sessionId) return <CachedResponseDetailPage cachedResponseId={page.sessionId} />
    if (page.name === 'garage') return <GarageFilesPage />
    return chatPage
  }

  return (
    <AppShell currentPage={page.name} isAuthenticated={isAuthenticated} isLoggedIn={isLoggedIn}>
      {renderPage()}
    </AppShell>
  )
}
